import type { AlertRule } from '../types/gotypes'
import { refuse, type WhoToAsk } from './permissionReason'

/** The caller owns the alert (emails compare case-insensitively). */
export function isAlertOwner(alert: AlertRule | undefined, userEmail: string | null | undefined): boolean {
  return Boolean(alert) && (alert!.user || '').toLowerCase() === (userEmail || '').toLowerCase()
}

/**
 * Only an alert's owner changes its tables, condition, context or
 * recipients, or has the agent rewrite its check (mirrors UpdateAlertRule
 * and the setup-session route in goapi/pkg/server/alerts.go, which answer
 * 403). A Manager can still rename it, change its schedule, pause, resume
 * and delete it. Undefined for the owner.
 */
export function alertConditionLockedReason(alert: AlertRule | undefined, userEmail: string | null | undefined, who: WhoToAsk): string | undefined {
  if (!alert || isAlertOwner(alert, userEmail)) return undefined
  return refuse("You can't change the tables, condition or recipients of a colleague's alert.", who, { owner: alert.user }).reason
}

/**
 * A colleague's alert (in "My colleagues'", or opened from a bell row as a
 * recipient): a non-Manager may look but not edit, pause, delete or run it
 * (PUT, DELETE and evaluate are owner-or-Manager). Undefined for the owner
 * and for a Manager.
 */
export function colleagueAlertReason(alert: AlertRule | undefined, userEmail: string | null | undefined, isManager: boolean, who: WhoToAsk): string | undefined {
  if (!alert || isManager || isAlertOwner(alert, userEmail)) return undefined
  return refuse("You can't change a colleague's alert.", who, { owner: alert.user }).reason
}

/** Only the owner has the agent write an alert's check (setup-session is owner only), Managers included. */
export function alertSetupReason(alert: AlertRule | undefined, userEmail: string | null | undefined, who: WhoToAsk): string | undefined {
  if (!alert || isAlertOwner(alert, userEmail)) return undefined
  return refuse("You can't have the agent write the check of a colleague's alert.", who, { owner: alert.user }).reason
}
